import { asset } from "./assets";

interface LocalizedText {
  en: string;
  hi: string;
}

// Not exported: nothing outside this module needs the per-CTA shape, only
// the assembled SWARVED_CTAS value below (fallow's dead-code check flags
// an exported type with no importer).
interface SwarvedCta {
  label: string;
  href: string;
}

/**
 * `/swarved` (and `/swarved-HI`, redirected from as `/hi/swarved`): the
 * intro headline, composer line and three body paragraphs, verbatim from
 * the old site. `swarved-HI` renders byte-identical English copy (the two
 * pages differ only in their page-builder element ids and `lang`/canonical
 * meta), so `hi` mirrors `en` for every field here rather than carrying a
 * translation that never existed.
 */
export const SWARVED = {
  heading: {
    en: "The Encyclopedia of Spirituality",
    hi: "The Encyclopedia of Spirituality",
  },
  composedBy: {
    en: "Composed by Himalaya Yogi Sadguru Sadafal Deo Ji Maharaj",
    hi: "Composed by Himalaya Yogi Sadguru Sadafal Deo Ji Maharaj",
  },
  paragraphs: [
    {
      // Verbatim, including the old site's own stray closing curly quote
      // (U+201D) after "Swarved" with no matching opening quote.
      en: "Swarved” is the most profound spiritual text available today. It is written by the great Himalaya Yogi and the pioneer of Vihangam Yoga, Sadguru Sadafal Deo Ji Maharaj in the Himalayan caves in a state of Yoga Samadhi, i.e. oneness with the Supreme Soul.",
      hi: "Swarved” is the most profound spiritual text available today. It is written by the great Himalaya Yogi and the pioneer of Vihangam Yoga, Sadguru Sadafal Deo Ji Maharaj in the Himalayan caves in a state of Yoga Samadhi, i.e. oneness with the Supreme Soul.",
    },
    {
      en: "Swarved is the expression of actual experiences at the highest stage of Yoga. Written in the form of simple couplets in Hindi language, it is a profound collection of spiritual secrets unveiled. It is not written out some other reference books or some intellectual exercise but the first hand knowledge that descends from the Divine.",
      hi: "Swarved is the expression of actual experiences at the highest stage of Yoga. Written in the form of simple couplets in Hindi language, it is a profound collection of spiritual secrets unveiled. It is not written out some other reference books or some intellectual exercise but the first hand knowledge that descends from the Divine.",
    },
    {
      en: "The purport of this noble Treatise was written by the first successor Sadguru Dharmachandra Deo Ji Maharaj.",
      hi: "The purport of this noble Treatise was written by the first successor Sadguru Dharmachandra Deo Ji Maharaj.",
    },
  ],
} satisfies {
  heading: LocalizedText;
  composedBy: LocalizedText;
  paragraphs: readonly LocalizedText[];
};

/**
 * The two-slide "What is Swarved" infographic carousel from the old page.
 * Each URL is resolved through the fetched-asset manifest; a key the
 * manifest doesn't carry is dropped rather than shipping a dead CDN link
 * (every local path the manifest returns starts with "/media/", so a
 * pass-through - `asset()`'s fallback for an unknown key - never matches
 * that prefix).
 */
const RAW_INFOGRAPHICS: readonly { url: string; alt: string }[] = [
  {
    url: "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/r/898-505-898-425-0-40/43B282B5793BF381.png?v=250703010154",
    alt: "What is Swarved",
  },
  {
    url: "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/r/898-505-898-425-0-40/6A75D2BA39B71C0C.png?v=250703010154",
    alt: "Swarved, an introduction",
  },
];

export const SWARVED_INFOGRAPHICS: readonly { src: string; alt: string }[] =
  RAW_INFOGRAPHICS.map(({ url, alt }) => ({ src: asset(url), alt })).filter(
    (image) => image.src.startsWith("/media/"),
  );

/** The Google Play and App Store listings linked from the old page. */
export const SWARVED_APP_LINKS = {
  googlePlay: "https://play.google.com/store/apps/details?id=com.vihangamyoga",
  appStore: "https://apps.apple.com/us/app/vihangam-yoga/id1516963421",
} as const;

/**
 * The old page's three buttons, in order: a hard-copy request through the
 * contact form, the daily Swarved reading practice, and the quarterly
 * Akhand Path recitation.
 */
export const SWARVED_CTAS: readonly SwarvedCta[] = [
  { label: "Hard Copy", href: "/contact" },
  { label: "Join Swarved Reading", href: "/activities/daily-practice" },
  { label: "Swarved Akhand Path", href: "/activities/swarved-akhand-path" },
];
